// src/pages/CustomerLogin.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import {
  sendMagicLink,
  isEmailSignInLink,
  completeSignInWithEmailLink,
  signInWithGoogle,
  signInWithFacebook,
  signInWithYahoo
} from '../api/customerAuthService';
import '../styles/customerAuth.css';

function CustomerLogin() {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { currentUser, currentCustomer, salonId: contextSalonId } = useCustomerAuth();
  const { salonId: _rawUrlSalonId } = useParams();
  const urlSalonId = _rawUrlSalonId?.toLowerCase();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || `/BespokeBeauty/${urlSalonId || contextSalonId}/customer/home`;

  // Use the salonId from either the URL params or the context (always lowercase)
  const salonId = urlSalonId || contextSalonId?.toLowerCase();

  // Check if the URL is a sign-in link when the component mounts
  useEffect(() => {
    const checkEmailLink = async () => {
      if (isEmailSignInLink()) {
        setVerifying(true);

        // Try to get email from localStorage
        let emailForSignIn = localStorage.getItem('emailForSignIn');

        // If no email in storage, prompt the user
        if (!emailForSignIn) {
          const promptedEmail = window.prompt('Please provide your email for confirmation');
          if (!promptedEmail) {
            setVerifying(false);
            setError('Email confirmation was canceled');
            return;
          }
          emailForSignIn = promptedEmail;
        }

        try {
          await completeSignInWithEmailLink(emailForSignIn, salonId);
          // After successful sign-in, navigate to the intended page
          navigate(from, { replace: true });
        } catch (err) {
          console.error('Error completing sign-in:', err);
          setError('Failed to complete sign-in. Please try again.');
          setVerifying(false);
        }
      }
    };

    checkEmailLink();
  }, [salonId, from, navigate]);

  // If user is already logged in, redirect to dashboard
  useEffect(() => {
    if (currentUser && currentCustomer && !verifying) {
      navigate(from, { replace: true });
    }
  }, [currentUser, currentCustomer, navigate, from, verifying]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const handleSendMagicLink = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create the redirect URL for after authentication
      const redirectUrl = `${window.location.origin}/salon/${salonId}/verify-email`;

      await sendMagicLink(email, salonId, redirectUrl);
      setEmailSent(true);
    } catch (err) {
      console.error('Error sending magic link:', err);
      setError('Failed to send login link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setLoading(true);
    setError('');

    try {
      switch (provider) {
        case 'google':
          await signInWithGoogle(salonId);
          break;
        case 'facebook':
          await signInWithFacebook(salonId);
          break;
        case 'yahoo':
          await signInWithYahoo(salonId);
          break;
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }

      // After successful sign-in, navigate to the intended page
      navigate(from, { replace: true });
    } catch (err) {
      console.error(`Error signing in with ${provider}:`, err);

      // Set appropriate error message based on error
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login was canceled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Login popup was blocked. Please allow popups for this website.');
      } else {
        setError(`Failed to sign in with ${provider}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // If verifying, show loading state
  if (verifying) {
    return (
      <div className="customer-auth-container">
        <div className="customer-auth-card">
          <div className="customer-auth-loading">
            <div className="spinner"></div>
            <p>Verifying your email...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-auth-container">
      <div className="customer-auth-card">
        <h2>Welcome to our Salon</h2>

        {error && (
          <div className="customer-auth-error">
            {error}
          </div>
        )}

        {emailSent ? (
          <div className="magic-link-sent">
            <div className="success-icon">✓</div>
            <h3>Check your email</h3>
            <p>
              We've sent a login link to <strong>{email}</strong>.
              Click the link in the email to sign in.
            </p>
            <p className="small-text">
              Don't see it? Check your spam folder or
              <button
                className="text-button"
                onClick={() => setEmailSent(false)}
              >
                try again
              </button>
            </p>
          </div>
        ) : (
          <>
            <div className="customer-auth-section">
              <h3>Sign in with Magic Link</h3>
              <p>We'll send a secure link to your email to sign in - no password needed.</p>

              <form onSubmit={handleSendMagicLink}>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Your email address"
                    disabled={loading}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="customer-auth-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Login Link'}
                </button>
              </form>
            </div>

            <div className="customer-auth-divider">
              <span>or</span>
            </div>

            <div className="customer-auth-section">
              <h3>Continue with</h3>

              <div className="social-buttons">
                <button
                  onClick={() => handleSocialLogin('google')}
                  className="social-button google-button"
                  disabled={loading}
                >
                  <svg className="social-icon" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google
                </button>

                <button
                  onClick={() => handleSocialLogin('facebook')}
                  className="social-button facebook-button"
                  disabled={loading}
                >
                  <svg className="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
                  </svg>
                  Facebook
                </button>

                <button
                  onClick={() => handleSocialLogin('yahoo')}
                  className="social-button yahoo-button"
                  disabled={loading}
                >
                  <svg className="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0h24v24H0z" fill="none"/>
                    <path d="M13.187 24v-9.007l4.267-9.856 2.477-5.137H15.21l-2.69 6.118L9.753 0H5l4.782 10.145L9.77 24h3.417z" fill="#6001D2"/>
                  </svg>
                  Yahoo
                </button>
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
}

export default CustomerLogin;
